import { BlogSection } from '@/components/page-sections/blog';
import { BlogIndexSection } from '@/components/page-sections/blog-index';
import { BlogWidgetSection } from '@/components/page-sections/blog-widget';
import { CaseResultIndexSection } from '@/components/page-sections/case-result-index';
import { CaseResultWidgetSection } from '@/components/page-sections/case-result-widget';
import { GoogleReviewsSection } from '@/components/page-sections/google-reviews';
import { PageTitleSection } from '@/components/page-sections/page-title';
import type { PageSectionComponent } from '@/components/page-sections/types';
import type { DefaultBlockInput } from '@/lib/blocks/default-blocks';
import http from '@/lib/http';
import { cmsSectionTemplateResolvedPropsPath } from '@/lib/routes';
import { PageType } from '@/types/page';

const SECTION_COMPONENTS = new Map<string, PageSectionComponent>([
  ['google_reviews', GoogleReviewsSection],
  ['blog_widget', BlogWidgetSection],
  ['case_result_widget', CaseResultWidgetSection],
  ['blog', BlogSection],
  ['blog_index', BlogIndexSection],
  ['case_result_index', CaseResultIndexSection],
]);

const SYSTEM_MANAGED_SECTION: ReadonlySet<string> = new Set(['review_index']);

function resolveComponentFamily(sectionType?: string): PageSectionComponent | undefined {
  if (sectionType === 'page_title') {
    return PageTitleSection as unknown as PageSectionComponent;
  }

  return undefined;
}

export function getSectionComponent(
  code: string,
  sectionType?: string,
): PageSectionComponent | undefined {
  return SECTION_COMPONENTS.get(code) ?? resolveComponentFamily(sectionType);
}

export function isComponentBackedSection(code: string, sectionType?: string): boolean {
  return getSectionComponent(code, sectionType) !== undefined;
}

export function isBlockBackedSection(code: string, sectionType?: string): boolean {
  return !isComponentBackedSection(code, sectionType);
}

export function isSystemManagedSection(code: string): boolean {
  return SYSTEM_MANAGED_SECTION.has(code);
}

export function isPageTitleSectionType(sectionType?: string): boolean {
  return sectionType === 'page_title';
}

const TRANSIENT_PROPS_BY_SECTION_TYPE = new Map<string, readonly string[]>([
  ['page_title', ['title']],
]);

export function transientSectionProps(sectionType?: string): readonly string[] {
  return (sectionType && TRANSIENT_PROPS_BY_SECTION_TYPE.get(sectionType)) || [];
}

export function isSectionTypeHiddenOnPage(
  sectionType: string | undefined,
  pageType: string | undefined,
): boolean {
  if (isPageTitleSectionType(sectionType) && pageType === PageType.HOME) return true;
  return false;
}

export interface ResolveSectionPropsContext {
  pageId?: string;
}

export interface ResolvedSectionPropsResult {
  props: Record<string, unknown>;
  defaultBlocks?: DefaultBlockInput[];
}

export async function resolveSectionProps(
  templateCode: string,
  props: Record<string, unknown>,
  context?: ResolveSectionPropsContext,
): Promise<ResolvedSectionPropsResult> {
  const url = cmsSectionTemplateResolvedPropsPath(templateCode);
  const params = new URLSearchParams();

  if (props && Object.keys(props).length > 0) {
    params.append('props', JSON.stringify(props));
  }

  if (context?.pageId) {
    params.append('page_id', context.pageId);
  }

  const queryString = params.toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  try {
    const response = await http.get(fullUrl);
    const responseData = (response.data || {}) as Record<string, unknown>;
    const defaultBlocks = responseData.defaultBlocks as DefaultBlockInput[] | undefined;

    return {
      props: (responseData.props as Record<string, unknown> | undefined) ?? {},
      defaultBlocks,
    };
  } catch (error) {
    const status = (
      error as {
        response?: {
          status?: number;
        };
      }
    )?.response?.status;

    if (status === 404) {
      return { props };
    }

    throw error;
  }
}
